import React, { useState } from 'react';
import { useRouter } from 'next/router';
import useDidUpdateEffect from '@use-effect/did-update';
import slugify from 'slugify';
import { useSpring } from '@react-spring/web';
import { Switches, Cross } from '@solent-university/solent-icons';
import useBreakpoint from 'Common/hooks/useBreakpoint';
import { useTheme } from 'styled-components';

import Section from 'Elements/Section';
import Checkbox from 'Elements/Input/Checkbox';
import Hidden from 'Elements/Hidden';

import ListBlock from 'Components/Block/List';
import Grid, { Col } from 'Components/Grid';

import Dropdown from 'Elements/Input/Dropdown';

import {
    Filters,
    SelectWrapper,
    StyledIcon,
    FilterPillButton,
    InfoWrapper,
    PillWrapper,
    ModalPillWrapper,
    FilterButton,
    Icon,
    MobileMenu,
    Modal,
    MobileButtonWrapper,
    StyledHr,
    HeadingWrapper,
    Heading,
    CloseButton,
    ResultsText,
    ResultsNumber
} from './SolentStories.styled';

export interface ActiveFilter {
    id: string;
    type: string;
    label: string;
}

export interface Filter {
    id: string;
    name: string;
    label: string;
    value: string;
}

export interface FilterGroup {
    type: string;
    label: string;
    options: Filter[];
}

export interface Props {
    id?: string;
    filters: FilterGroup[];
    initialActiveFilters: ActiveFilter[];
    numberOfResults?: number;
}

const SolentStoryListing: React.FC<Props> = props => {
    const { id, filters, initialActiveFilters, numberOfResults } = props;
    const { breakpoints, colors } = useTheme();
    const isMobile = useBreakpoint(breakpoints.medium.value);

    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>(initialActiveFilters);
    const router = useRouter();

    const mobileSpring = useSpring({
        left: mobileMenuOpen ? '0' : '+100%',
        right: mobileMenuOpen ? '0%' : '-100%',
        config: { mass: 1, tension: 180, friction: 20 }
    });

    useDidUpdateEffect(() => {
        filterResults();
    }, [activeFilters]);

    const addFilter = (id: string, type: string, label: string) => {
        setActiveFilters([...activeFilters, { id, type, label }]);
    };

    const removeFilter = (id: string) => {
        const findAndRemoveFilter = (filters: ActiveFilter[]): ActiveFilter[] => {
            const updatedFilters: ActiveFilter[] = [];

            for (const filter of filters) {
                if (filter.id === id) {
                    continue;
                } else {
                    updatedFilters.push(filter);
                }
            }

            return updatedFilters;
        };

        setActiveFilters(findAndRemoveFilter(activeFilters));
    };

    const handleFilter = (id: string, checked: boolean, type: string, label: string) => {
        if (checked) {
            addFilter(id, type, label);
        } else {
            removeFilter(id);
        }
    };

    const filterResults = () => {
        const query: Record<string, string | number> = {
            page: 1
        };

        activeFilters.forEach(filter => {
            if (query[filter.type]) {
                query[filter.type] += `,${filter.id}`;
            } else {
                query[filter.type] = filter.id;
            }
        });

        router.push(
            {
                pathname: router.pathname,
                query
            },
            undefined,
            { scroll: false }
        );
    };

    const generateCheckboxes = (filter: FilterGroup) =>
        filter.options.map(item => {
            const activeFilter = activeFilters.find(({ id }) => id === item.id);
            return (
                <React.Fragment key={`filter-${item.id}`}>
                    <Checkbox
                        defaultChecked={!!activeFilter}
                        handleCheckedChange={checked =>
                            handleFilter(item.id, checked, filter.type, item.label)
                        }
                        {...item}
                    />
                </React.Fragment>
            );
        });

    const filtersJSX = (
        <>
            {filters.map(filter => (
                <SelectWrapper key={filter.type} $type={filter.type}>
                    <Dropdown
                        textColor={{
                            label: isMobile
                                ? colors.primaryContrast.hex
                                : colors.secondary.tints[8].hex
                        }}
                        button={{
                            ariaLabel: filter.label,
                            label: filter.label
                        }}
                        id={filter.type}
                    >
                        <ListBlock
                            fieldset={true}
                            id={`fieldset-${slugify(filter.type, {
                                lower: true
                            })}`}
                            maxHeight={filter.options.length > 8}
                        >
                            {generateCheckboxes(filter)}
                        </ListBlock>
                    </Dropdown>
                </SelectWrapper>
            ))}
        </>
    );

    const pillsJSX = (
        <>
            {activeFilters.map(item => (
                <React.Fragment key={`filter-${item.id}`}>
                    <FilterPillButton
                        onClick={() => removeFilter(item.id)}
                        aria-label={`Remove ${item.id} course filter`}
                    >
                        {`${item.label}`}
                        <StyledIcon />
                    </FilterPillButton>
                </React.Fragment>
            ))}
        </>
    );

    return (
        <Section
            id="filters"
            paddingTop={0.5}
            paddingBottom="none"
            background
            backgroundColor={colors.campaigns.clearing.secondary.hex}
        >
            <form onSubmit={e => e.preventDefault()} id={id}>
                <Hidden defaultHidden={true} medium={false} renderHTML={false}>
                    <Grid>
                        <Col l={9}>
                            <Filters>{filtersJSX}</Filters>
                        </Col>
                    </Grid>
                </Hidden>
                <Hidden base={false} medium={true} renderHTML={false}>
                    <Grid>
                        <Col>
                            <FilterButton
                                aria-label="Click to access mobile filter menu"
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                aria-expanded={mobileMenuOpen}
                                aria-controls="mobile-menu"
                            >
                                <Icon as={Switches} />
                                Filter stories
                            </FilterButton>
                        </Col>
                    </Grid>
                </Hidden>
                <Grid>
                    <Col>
                        <InfoWrapper $pills={activeFilters.length >= 1}>
                            {activeFilters.length >= 1 && <PillWrapper>{pillsJSX}</PillWrapper>}
                            <ResultsText>
                                Showing
                                <ResultsNumber> {numberOfResults} stories </ResultsNumber>
                                that fit your criteria
                            </ResultsText>
                        </InfoWrapper>
                    </Col>
                </Grid>
                {mobileMenuOpen && (
                    <Modal style={mobileSpring} id="mobile-menu">
                        <MobileMenu>
                            <HeadingWrapper>
                                <CloseButton
                                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                    aria-label="Click to close mobile menu"
                                    aria-controls="mobile-menu"
                                    aria-expanded={mobileMenuOpen}
                                >
                                    <Icon as={Cross} />
                                </CloseButton>
                                <Heading>Filter courses</Heading>
                            </HeadingWrapper>
                            <Grid>
                                <Col>
                                    {filtersJSX}
                                    <StyledHr />
                                    <ModalPillWrapper>{pillsJSX}</ModalPillWrapper>
                                </Col>
                            </Grid>
                            <MobileButtonWrapper>
                                <FilterButton
                                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                    aria-label="Click to apply filters and close mobile menu"
                                    aria-controls="mobile-menu"
                                    aria-expanded={mobileMenuOpen}
                                >
                                    <Icon as={Switches} />
                                    Apply Filter
                                </FilterButton>
                            </MobileButtonWrapper>
                        </MobileMenu>
                    </Modal>
                )}
            </form>
        </Section>
    );
};

export default SolentStoryListing;
